import React, { useState } from "react";
import Button from "components/common/atoms/Button";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { t } from "components/CentralDataMangement/translation/Translation";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import { useFormContext } from "../../context/Context";
import {
  DRAFT,
  GENERAL,
  INTERNAL_INFO,
  NEXT,
  NEXT_STEP,
  PLANNING_DRAFT_MSG,
  PREVIOUS_STEP,
  STORE_PLANNING_DETAILS,
  SUBMIT,
  UPDATE_FIELD_ERROR,
  UPDATE_TAB_DETAILS,
  UPDATE_TAB_ERROR,
} from "../../annotation/PlanningConstants";
import { scrollToTop } from "components/common/services/ValidationService";
import { getNextTabId } from "components/common/utlis/TabUtility";
import { ApiCall } from "components/common/services/ApiServices";
import { COMPANY_MICROSERVICE, SUCCESS } from "Constants";
import CustomNotify from "components/common/atoms/CustomNotify";
import { validateMandatoryFields, validateScheduleTimings } from "./FormValidation";
import { validateBasicDetails } from "./PlanningFieldValidation";
import { validateTimings } from "./validateTimings";
import { Weekdays } from "components/common/utlis/PlanningUtility";

export interface MandatoryFields {
  [key: string]: string[];
}

interface NavProps {
  validStatus: (value: { isValid: boolean; type: string }) => void;
  isLoading?: boolean;
}

const Navigation: React.FC<NavProps> = ({ validStatus, isLoading = false }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { state, dispatch, isViewMode } = useFormContext();
  const { planningId } = useParams<{ planningId: string }>();
  const location = useLocation();
  const isClone = location.pathname.includes("clone");

  const currentTab = state.tabDetails.find((tab) => tab.showStatus);
  const isGeneralTab = currentTab && currentTab.id === GENERAL;
  const isInternalInfoTab = currentTab && currentTab.id === INTERNAL_INFO;
  const formStatus = state.internalInfoDetails.formStatus;

  const handleNextStep = () => {
    dispatch({ type: NEXT_STEP });
    if (!isViewMode) {
      const isBasicDetailsValid = validateBasicDetails(
        state.basicDetails,
        dispatch,
        NEXT
      );
      if (isBasicDetailsValid) {
        handleSubmitFormData(NEXT);
      }
    }
  };

  const handlePreviousStep = () => {
    dispatch({ type: PREVIOUS_STEP });
  };

  // Check if there is a companyId in the URL
  const urlParams = new URLSearchParams(window.location.search);
  const destination = urlParams.get("destination");

  const handleSubmitFormData = async (type: string) => {
    if (type === SUBMIT) {
      setLoading(true);
    }
    const tabId = getNextTabId(state.tabDetails);

    const url = STORE_PLANNING_DETAILS;

    //excluding tabDetails,dropDowndata,errors from state
    const { tabDetails, dropDowndata, errors, ...filteredState } = state;

    // Send contract from/to as LOCAL YYYY-MM-DD. The date picker holds Date
    // objects; sending them raw serializes to UTC (toISOString), which rolls the
    // date back a day for +offset timezones (Belgium/IST) -> Mon 20 became Sun 19.
    const toLocalYmd = (d: any) => {
      if (!d) return d;
      const dt = d instanceof Date ? d : new Date(d);
      if (isNaN(dt.getTime())) return d;
      const mm = `${dt.getMonth() + 1}`.padStart(2, "0");
      const dd = `${dt.getDate()}`.padStart(2, "0");
      return `${dt.getFullYear()}-${mm}-${dd}`;
    };

    const planningData = {
      ...filteredState,
      basicDetails: {
        ...filteredState.basicDetails,
        from: toLocalYmd(filteredState.basicDetails?.from),
        to: toLocalYmd(filteredState.basicDetails?.to),
      },
    };

    const data = {
      id: planningId ? planningId : null,
      planningData: planningData,
      formStatus: type === SUBMIT ? 1 : state.internalInfoDetails.formStatus,
      type,
      mode: isClone ? "extend" : "edit"
    };
    const response = await ApiCall.service(
      url,
      "POST",
      data,
      false,
      COMPANY_MICROSERVICE
    );
    if (response?.status === 200) {
      if (type === DRAFT) {
        CustomNotify({
          type: SUCCESS,
          message: t(PLANNING_DRAFT_MSG),
        });
        destination ? navigate(`${destination}`) : navigate("/manage-planning");
      } else if (type === SUBMIT) {
        CustomNotify({
          type: "success",
          message: response.data["msg"],
        });
        destination ? navigate(`${destination}`) : navigate("/manage-planning");
      } else {
        navigate(`/planning/edit/${response.data["id"]}?tabId=${tabId}`);
      }
    } else if (response.status == 'planning-validation-error') {
      CustomNotify({
        type: "error",
        message: response.message,
        autoClose: 5000
      });

      response.data.forEach((item: { code: string; message: string }) => {
        if (item.code === 'planning schedule') {
          dispatch({
            type: UPDATE_TAB_ERROR,
            tabIndex: 3,
            error: true,
          });
          dispatch({
            type: UPDATE_FIELD_ERROR,
            field: "dateError",
            error: item.message,
          });
        } else {
          dispatch({
            type: UPDATE_FIELD_ERROR,
            field: item.code,
            error: item.message,
          });
        }
      });

    }
    else {
      CustomNotify({
        type: "error",
        message: response.exception,
        autoClose: false
      });
    }
    setLoading(false);
  };

  const handleDraft = () => {
    const update = state.tabDetails.map((tab: any, index: any) => ({
      ...tab,
      draft: true,
    }));

    dispatch({ type: UPDATE_TAB_DETAILS, tabDetails: update });
    const valid = validateMandatoryFields(state, dispatch, DRAFT);
    const isBasicDetailsValid = validateBasicDetails(
      state.basicDetails,
      dispatch,
      DRAFT
    );
    if (!valid || !isBasicDetailsValid) {
      validStatus({ isValid: false, type: DRAFT });
      scrollToTop();
    } else {
      validStatus({ isValid: true, type: DRAFT });
      handleSubmitFormData(DRAFT);
    }
  };

  // const validateTimingFields = () => {
  //   const timingErrors: number[] = [];

  //   Object.keys(state.timeTableDetails.timeTable).forEach((day) => {
  //     const dayNumber = Number(day);
  //     const { hasError } = validateTimings(dayNumber as Weekdays, state.timeTableDetails.timeTable)
  //     if (hasError) {
  //       timingErrors.push(dayNumber);
  //     }
  //   });
  //   if (timingErrors.length > 0) {
  //     dispatch({
  //       type: UPDATE_TAB_ERROR,
  //       tabIndex: 3,
  //       error: true,
  //     });
  //     dispatch({
  //       type: UPDATE_FIELD_ERROR,
  //       field: "dateError",
  //       error: t("Please check the highlighted fields and enter a valid time"),
  //     });
  //     return false;
  //   } else {
  //     dispatch({
  //       type: UPDATE_FIELD_ERROR,
  //       field: "dateError",
  //       error: t(""),
  //     });
  //     return true;
  //   }
  // }

  const handleSubmit = () => {
    const update = state.tabDetails.map((tab, index) => ({
      ...tab,
      draft: false,
    }));
    dispatch({ type: UPDATE_TAB_DETAILS, tabDetails: update });
    const valid = validateMandatoryFields(state, dispatch, SUBMIT);
    const isBasicDetailsValid = validateBasicDetails(
      state.basicDetails,
      dispatch,
      SUBMIT
    );

    const validScheduleTimings = validateScheduleTimings(state.timeTableDetails.timeTable,
      state.basicDetails.from,
      state.basicDetails.to,
      dispatch);

    if (!valid || !isBasicDetailsValid || !validScheduleTimings) {
      validStatus({ isValid: false, type: SUBMIT });
      scrollToTop();
    } else {
      validStatus({ isValid: true, type: SUBMIT });
      handleSubmitFormData(SUBMIT);
    }
  };

  return (
    <>
      <div className="d-none d-md-block d-lg-block py-4">
        <div className="row mt-3">
          <div className="col-md-6 align-self-center">
            {isGeneralTab && (
              <Link
                to=""
                className="back-btn text-decoration-underline"
                onClick={() => navigate(-1)}
              >
                {t("Back")}
              </Link>
            )}
            {!isGeneralTab && (
              <Button
                type="button"
                title={t("Back")}
                handleClick={handlePreviousStep}
                className="backBtn p-0 text-decoration-underline border-0"
              />
            )}
          </div>
          <div className="col-md-6 ">
            {!loading ? (
              <>
                {!isViewMode &&
                  <Button
                    type="submit"
                    title={isInternalInfoTab ? t("Submit") : t("Next")}
                    handleClick={isInternalInfoTab ? handleSubmit : handleNextStep}
                    className="float-end form-button ms-3 shadow-none"
                  />
                }
                {
                  isViewMode &&
                  <Button
                    type="submit"
                    title={t("Next")}
                    handleClick={handleNextStep}
                    className="float-end form-button ms-3 shadow-none"
                  />
                }
              </>
            ) : (
              <LoadingIcon
                iconType="bars"
                color="#e5007d"
                className="ms-auto mb-3"
                height={"3vw"}
                width={"3vw"}
              />
            )}
            {!isViewMode && formStatus !== 1 && !isInternalInfoTab && (
              <Button
                type="submit"
                title={t("Save as draft")}
                handleClick={handleDraft}
                className="float-end form-button shadow-none"
              />
            )}
          </div>
        </div>
      </div>
      {/*PWA */}
      <div className="d-block d-md-none d-lg-none my-3">
        <div className="row mt-3">
          <div className="col-md-6 mb-3">
            {formStatus !== 1 && !isInternalInfoTab && (
              <Button
                type="submit"
                title={t("Save as draft")}
                handleClick={handleDraft}
                className="btn float-end form-button shadow-none rounded-3 w-100"
              />
            )}
          </div>
          <div className="col-md-12 mb-3">
            <div className="row">
              <div className="col-6 align-self-center">
                {isGeneralTab && (
                  <Link
                    to="/manage-planning"
                    className="back-btn text-decoration-underline"
                  >
                    {t("Back")}
                  </Link>
                )}
                {!isGeneralTab && (
                  <Button
                    type="button"
                    title={t("Back")}
                    handleClick={handlePreviousStep}
                    className="btn backBtn p-0 text-decoration-underline"
                  />
                )}
              </div>
              <div className="col-6">
                {loading ? (
                  <>
                    {!isViewMode &&
                      <Button
                        type="submit"
                        title={isInternalInfoTab ? t("Submit") : t("Next")}
                        handleClick={isInternalInfoTab ? handleSubmit : handleNextStep}
                        className="btn float-end form-button ms-3 shadow-none rounded-3"
                      />
                    }
                    {
                      isViewMode &&
                      <Button
                        type="submit"
                        title={t("Next")}
                        handleClick={handleNextStep}
                        className="btn float-end form-button ms-3 shadow-none rounded-3"
                      />
                    }
                  </>

                ) : (
                  <LoadingIcon
                    iconType="bars"
                    color="#ff4dae"
                    className="float-end"
                    height={"3vw"}
                    width={"3vw"}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
